import * as R from 'ramda'
import { useCallback, useMemo, useState } from 'react'
import {
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material'
import { AddLinkRounded } from '@mui/icons-material'
import { ListItemCriticalTextButton } from '$common/buttons'
import { PageWithAppBar } from '$common/page-with-app-bar'
import { AppBarBackTitle } from '$common/app-bar/app-bar-back-title'
import { SettingsSection } from '$common/settings-section'
import { UserListItemSkeleton } from '$common/user-list-item'
import { InviteLinkListItem } from './invite-link-list-item'
import { t } from '$i18n'
import { useHallInvitationLinks } from '$logic/hall/react-hook'
import { closeHallInviteLinks, openCreateInviteLink } from '$logic/router'
import { deleteInviteLinks } from '$logic/hall'

const LOADING_ITEM_COUNT = 3

function AddInviteLinkButton({ onClick }: { onClick: () => unknown }) {
  return (
    <SettingsSection>
      <ListItemButton onClick={onClick}>
        <ListItemIcon>
          <AddLinkRounded color="secondary" sx={{ mx: 1 }} />
        </ListItemIcon>

        <ListItemText
          primary={t('inviteLinks.list.createNewLink')}
          primaryTypographyProps={{
            color: 'secondary',
            variant: 'body2',
          }}
        />
      </ListItemButton>
    </SettingsSection>
  )
}

interface Props {
  roomId: string
}

export function InviteLinks({ roomId }: Props) {
  const { items: links, loading: linksLoading } = useHallInvitationLinks(roomId)

  const [deletingAllRevoked, setDeletingAllRevoked] = useState(false)

  const { false: validLinks = [], true: revokedLinks = [] } = useMemo(
    () =>
      R.groupBy(
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        R.prop('isRevoked'),
        links || [],
      ),
    [links],
  )

  const deleteAllRevokedLinks = useCallback(async () => {
    if (deletingAllRevoked) return
    setDeletingAllRevoked(true)
    deleteInviteLinks(
      roomId,
      revokedLinks.map(link => link.link),
    )
      .finally(() => {
        setDeletingAllRevoked?.(false)
      })
      .catch(() => {
        //  TODO: Show toast
      })
  }, [deletingAllRevoked, roomId, revokedLinks])

  return (
    <PageWithAppBar
      appBar={
        <AppBarBackTitle
          onBack={closeHallInviteLinks}
          title={t('inviteLinks.list.inviteLinks')}
        />
      }
    >
      <AddInviteLinkButton onClick={() => openCreateInviteLink(roomId)} />

      <SettingsSection headerText={t('inviteLinks.list.createdLinks')}>
        {linksLoading ? (
          <>{R.repeat(<UserListItemSkeleton />, LOADING_ITEM_COUNT)}</>
        ) : validLinks.length === 0 ? (
          <Stack
            alignItems="center"
            justifyContent="center"
            sx={{ height: '200px' }}
          >
            <Typography variant="body2" color="gray">
              {t('inviteLinks.list.noLink')}
            </Typography>
          </Stack>
        ) : (
          <>
            {validLinks?.map(link => (
              <InviteLinkListItem key={link.link} inviteLink={link} />
            ))}
          </>
        )}
      </SettingsSection>

      {revokedLinks?.length > 0 && (
        <>
          <SettingsSection headerText={t('inviteLinks.list.revokedLinks')}>
            <>
              {revokedLinks.map(link => (
                <InviteLinkListItem key={link.link} inviteLink={link} />
              ))}
            </>
          </SettingsSection>
          <SettingsSection>
            <ListItemCriticalTextButton
              onClick={deleteAllRevokedLinks}
              text={t('inviteLinks.list.deleteAllRevokedLinks')}
              typographyProps={{ variant: 'subtitle3', color: 'error' }}
              loading={deletingAllRevoked}
            />
          </SettingsSection>
        </>
      )}
    </PageWithAppBar>
  )
}
